// resources/js/Pages/Formula/SchemaBuilder.js - UPDATED FOR FLAT + ARRAYS DESIGN
import React, { useReducer, useEffect, useCallback, useRef } from 'react';
import { v4 as uuid } from 'uuid';
import ElementPalette from './ElementPalette';
import PropertiesPane from './PropertiesPane';
import TreeNode from './TreeNode';
import { traverseAndUpdate } from './utils/schemaUpdates';

/* ---------- find node helper ---------- */
const findNode = (tree, id) => {
  if (!id) return null;
  if (tree.id === id) return tree;
  for (const child of tree.children || []) {
    const found = findNode(child, id);
    if (found) return found;
  }
  return null;
};

/* ---------- pure-tree helpers ---------- */
const addNode = (tree, parentId, newNode) => {
  if (tree.id === parentId) {
    return { ...tree, children: [...(tree.children || []), newNode] };
  }
  if (!tree.children) return tree;
  return { ...tree, children: tree.children.map(c => addNode(c, parentId, newNode)) };
};

const updateNode = (tree, nodeId, patch) => {
  if (tree.id === nodeId) return { ...tree, ...patch };
  if (!tree.children) return tree;
  return { ...tree, children: tree.children.map(c => updateNode(c, nodeId, patch)) };
};

const deleteNode = (tree, nodeId) => {
  if (tree.id === nodeId) {
    // Can't delete root node
    return tree;
  }
  if (!tree.children) return tree;
  return { 
    ...tree, 
    children: tree.children
      .filter(c => c.id !== nodeId)
      .map(c => deleteNode(c, nodeId))
  };
};

/* ---------- root factory ---------- */
const emptyRoot = () => ({
  id: uuid(),
  type: 'root',
  key: 'Root',
  children: [],
});

// ✅ NEW: Convert field to array
const convertFieldToArray = (tree, nodeId, displayType) => {
  return traverseAndUpdate(tree, (node) => {
    if (node.id === nodeId && node.type === 'field') {
      return {
        ...node,
        type: 'array',
        display: displayType,
        instruction: '', // User must fill this in
        children: []
      };
    }
    return node;
  });
};

/* ---------- main component ---------- */
export default function SchemaBuilder({ value, onSchemaChange, variables }) {
  const updateTimeoutRef = useRef(null);
  
  /* ——— schema reducer ——— */
  const [schema, dispatch] = useReducer(
    (state, action) => {
      console.log('🔄 Schema action:', action.type);
      switch (action.type) {
        case 'ADD_NODE':
          return addNode(state, action.parentId, action.newNode);
        case 'UPDATE_NODE':
          return updateNode(state, action.nodeId, action.patch);
        case 'DELETE_NODE':
          return deleteNode(state, action.nodeId);
        case 'CONVERT_TO_ARRAY':
          return convertFieldToArray(state, action.nodeId, action.displayType);
        case 'REPLACE_SCHEMA':
          return action.schema;
        default:
          return state;
      }
    },
    value && value.id ? value : emptyRoot()
  );

  /* ——— NEW: Watch for value prop changes and update internal state ——— */
  useEffect(() => {
    // If value prop changes and has a valid schema structure, update internal state
    if (value && value.id && (value.type === 'root' || value.type === 'list')) {
      console.log('📥 Importing new schema into SchemaBuilder');
      dispatch({ type: 'REPLACE_SCHEMA', schema: value });
    }
  }, [value]);

  /* ——— selection state: id only ——— */
  const [selectedId, setSelectedId] = React.useState(null);
  const selectedNode = findNode(schema, selectedId);

  /* auto-select root on mount */
  useEffect(() => {
    if (!selectedId) setSelectedId(schema.id);
  }, [schema.id, selectedId]);

  /* ✅ FIXED: Debounced schema updates to prevent rapid re-renders */
  useEffect(() => {
    // Clear existing timeout
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    
    // Set new timeout for debounced update
    updateTimeoutRef.current = setTimeout(() => {
      console.log('📡 Sending schema update to parent');
      onSchemaChange?.(schema);
    }, 100); // 100ms debounce
    
    // Cleanup timeout on unmount
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [schema]);

  /* ——— helpers to add/delete nodes ——— */
  const addField = useCallback(() => {
    if (!selectedId || !selectedNode) return;
    
    // Can add fields to root or arrays
    if (selectedNode.type !== 'root' && selectedNode.type !== 'array' && selectedNode.type !== 'list') return;
    
    const newId = uuid();
    dispatch({
      type: 'ADD_NODE',
      parentId: selectedId,
      newNode: {
        id: newId,
        type: 'field',
        key: 'new_field',
        instruction: 'Provide detailed information about this field',
        valueType: 'string',
      },
    });
    console.log('➕ ADD_FIELD under', selectedNode?.key);
    setSelectedId(newId); // auto-select the new node
  }, [selectedId, selectedNode]);

  // ✅ NEW: Convert field to array
  const convertToArray = useCallback((nodeId, displayType) => {
    console.log('🔄 CONVERT_TO_ARRAY:', nodeId, 'to', displayType);
    dispatch({
      type: 'CONVERT_TO_ARRAY',
      nodeId: nodeId,
      displayType: displayType
    });
    // Keep the same node selected to see the changes
  }, []);

  const deleteSelectedNode = useCallback(() => {
    if (!selectedId || selectedId === schema.id) return; // Can't delete root
    console.log('🗑️ DELETE_NODE:', selectedId);
    dispatch({ type: 'DELETE_NODE', nodeId: selectedId });
    setSelectedId(schema.id); // Select root after deletion
  }, [selectedId, schema.id]);

  /* ——— update props ——— */
  const patchNode = useCallback((id, patch) => {
    console.log('✏️ UPDATE_NODE:', id, patch);
    dispatch({ type: 'UPDATE_NODE', nodeId: id, patch });
  }, []);

  /* ——— render ——— */
  return (
    <div className="row schema-builder">
      {/* Palette */}
      <div className="col-md-3">
        <ElementPalette
          selectedNode={selectedNode}
          onAddField={addField}
          onDelete={deleteSelectedNode}
          onConvertToArray={convertToArray}
        />
      </div>

      {/* Tree */}
      <div className="col-md-4">
        <div className="card">
          <div className="card-header">
            <h6 className="mb-0">Schema Structure</h6>
            <small className="text-muted">Click nodes to select and edit</small>
          </div>
          <div className="card-body p-2" style={{ maxHeight: 500, overflowY: 'auto' }}>
            <TreeNode
              node={schema}
              selectedId={selectedId}
              onSelect={node => {
                console.log('🎯 SELECT', node.key);
                setSelectedId(node.id);
              }}
            />
          </div>
        </div>
      </div>

      {/* Properties + JSON preview */}
      <div className="col-md-5">
        <PropertiesPane 
          node={selectedNode} 
          variables={variables} 
          onChange={patchNode}
          onConvertToArray={convertToArray}
        />
        
        {/* JSON Preview */}
        <details className="mt-3">
          <summary className="h6 cursor-pointer">
            Raw JSON Preview
            <span className="ms-2 badge bg-secondary">Debug</span>
          </summary>
          <div className="mt-2">
            <pre className="bg-light p-3 rounded small border" style={{ maxHeight: 300, overflowY: 'auto' }}>
              {JSON.stringify(schema, null, 2)}
            </pre>
          </div>
        </details>
        
        {/* ✅ DEBUG: Show update status */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mt-2 p-2 bg-info bg-opacity-10 rounded">
            <small className="text-info">
              🐛 Schema nodes: {countNodes(schema)} | Selected: {selectedNode?.key || 'none'} | Type: {selectedNode?.type || 'unknown'}
            </small>
          </div>
        )}
      </div>
    </div>
  );
}

// Helper function to count nodes (for debugging)
function countNodes(node) {
  if (!node) return 0;
  let count = 1; // count this node
  if (node.children) {
    for (const child of node.children) {
      count += countNodes(child);
    }
  }
  return count;
}